import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material'
import { Search, ChevronRight } from '@mui/icons-material'

const DataTable = ({
  columns,
  data = [],
  loading = false,
  pagination = true,
  searchable = true,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data available',
  ariaLabel = 'Data table',
  onRowClick,
  rowKey = 'id',
  initialSortBy,
  initialSortOrder = 'asc',
  stickyHeader = false,
  maxHeight,
  mobileColumns = 3, // Number of columns to show in mobile card view
  // Server-side pagination props
  serverSide = false,
  totalCount = 0,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  onPaginationChange,
  onSearch, // Callback for server-side search
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [internalPage, setInternalPage] = useState(0)
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState(initialSortBy)
  const [sortOrder, setSortOrder] = useState(initialSortOrder)

  // Use controlled or internal state
  const page = serverSide ? (controlledPage ?? 0) : internalPage
  const rowsPerPage = serverSide ? (controlledRowsPerPage ?? 10) : internalRowsPerPage
  const setPage = serverSide
    ? (newPage) => onPaginationChange?.({ page: newPage, pageSize: rowsPerPage })
    : setInternalPage
  const setRowsPerPage = serverSide
    ? (newSize) => onPaginationChange?.({ page: 0, pageSize: newSize })
    : setInternalRowsPerPage

  // Track if this is the first render to skip initial search call
  const isFirstRender = useRef(true)
  const onSearchRef = useRef(onSearch)

  // Keep onSearchRef up to date
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  // Debounced server-side search
  useEffect(() => {
    if (!serverSide || !onSearchRef.current) return
    // Skip the initial render to avoid double API call
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeoutId = setTimeout(() => {
      onSearchRef.current?.(searchTerm)
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [searchTerm, serverSide])

  const handleSort = (column) => {
    if (!column.sortable) return
    const isAsc = sortBy === column.field && sortOrder === 'asc'
    setSortOrder(isAsc ? 'desc' : 'asc')
    setSortBy(column.field)
  }

  // For server-side pagination, data is already filtered/sorted/paginated by backend
  const filteredData = serverSide ? data : data.filter((row) => {
    if (!searchTerm) return true
    return columns.some((col) => {
      const value = row[col.field]
      if (value === null || value === undefined) return false
      return String(value).toLowerCase().includes(searchTerm.toLowerCase())
    })
  })

  const sortedData = serverSide ? filteredData : [...filteredData].sort((a, b) => {
    if (!sortBy) return 0
    const aVal = a[sortBy]
    const bVal = b[sortBy]
    if (aVal === bVal) return 0
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1
    const comparison = aVal < bVal ? -1 : 1
    return sortOrder === 'asc' ? comparison : -comparison
  })

  // For server-side, don't slice - backend already paginated
  const paginatedData = serverSide
    ? sortedData
    : (pagination ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sortedData)

  // Get display columns for mobile (exclude action columns)
  const displayColumns = columns.filter(col => col.field !== 'actions').slice(0, mobileColumns)
  const actionColumn = columns.find(col => col.field === 'actions')

  // Mobile Card View
  const MobileCardView = () => (
    <Stack spacing={2}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : paginatedData.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Box>
      ) : (
        paginatedData.map((row, index) => (
          <Card
            key={row[rowKey] || index}
            onClick={() => onRowClick?.(row)}
            {...(onRowClick
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onRowClick(row)
                    }
                  },
                }
              : {})}
            sx={{
              cursor: onRowClick ? 'pointer' : 'default',
              '&:hover': onRowClick ? { boxShadow: 2 } : {},
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  {displayColumns.map((column, colIndex) => {
                    const value = row[column.field]
                    const rendered = column.renderCell
                      ? column.renderCell(value, row, page * rowsPerPage + index)
                      : (value ?? '-')

                    if (colIndex === 0) {
                      return (
                        <Typography key={column.field} variant="subtitle1" fontWeight={600} mb={1}>
                          {rendered || '-'}
                        </Typography>
                      )
                    }

                    return (
                      <Box key={column.field} display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                          {column.headerName}:
                        </Typography>
                        <Typography variant="body2">
                          {rendered || '-'}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {actionColumn && actionColumn.renderCell && (
                    <Box onClick={(e) => e.stopPropagation()}>
                      {actionColumn.renderCell(null, row)}
                    </Box>
                  )}
                  {onRowClick && !actionColumn && (
                    <ChevronRight color="action" />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  )

  // Desktop Table View
  const DesktopTableView = () => (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: maxHeight,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        overflowX: 'auto',
      }}
    >
      <Table stickyHeader={stickyHeader} size="medium" aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.field}
                align={column.align || 'left'}
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  width: column.width,
                  minWidth: column.minWidth,
                }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sortBy === column.field}
                    direction={sortBy === column.field ? sortOrder : 'asc'}
                    onClick={() => handleSort(column)}
                  >
                    {column.headerName}
                  </TableSortLabel>
                ) : (
                  column.headerName
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <CircularProgress size={32} />
              </TableCell>
            </TableRow>
          ) : paginatedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedData.map((row, index) => (
              <TableRow
                key={row[rowKey] || index}
                hover
                onClick={() => onRowClick?.(row)}
                {...(onRowClick
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onRowClick(row)
                        }
                      },
                    }
                  : {})}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell key={column.field} align={column.align || 'left'}>
                    {column.renderCell
                      ? column.renderCell(row[column.field], row, page * rowsPerPage + index)
                      : (row[column.field] ?? '-')}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <Box>
      {searchable && (
        <Box mb={2}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              // Only reset page for client-side pagination
              if (!serverSide) {
                setInternalPage(0)
              }
            }}
            fullWidth={isMobile}
            type="search"
            inputProps={{ 'aria-label': searchPlaceholder }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          />
        </Box>
      )}

      {isMobile ? <MobileCardView /> : <DesktopTableView />}

      {pagination && !loading && (serverSide ? totalCount > 0 : data.length > 0) && (
        <TablePagination
          component="div"
          count={serverSide ? totalCount : filteredData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10)
            if (serverSide) {
              setRowsPerPage(newSize)
            } else {
              setInternalRowsPerPage(newSize)
              setInternalPage(0)
            }
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={isMobile ? '' : 'Rows per page:'}
          sx={{
            '.MuiTablePagination-selectLabel': {
              display: { xs: 'none', sm: 'block' },
            },
            '.MuiTablePagination-select': {
              display: { xs: 'none', sm: 'block' },
            },
          }}
        />
      )}
    </Box>
  )
}

export default DataTable
